/**
 * iter45 (M3) — Tournament-scoped Settings tab.
 *
 * Contains settings that apply to the currently-viewed tournament only:
 * - Tournament timezone (per-tournament display)
 * - "Tournament locked" banner (only when complete)
 * - Admin participation toggle (depends on draft for THIS tournament)
 * - Draft management (clear current draft)
 * - Restore original seeding (per-tournament)
 *
 * League-wide settings (invites, spectator mode, admin transfer, smack stream
 * admin) live in LeagueSettingsTab.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Flag, Trophy, FileText, Download } from "lucide-react";
import TournamentTimezoneCard from "@/components/TournamentTimezoneCard";
import RestoreSeedingCard from "@/components/RestoreSeedingCard";
import ShareRecapCard from "@/components/admin/ShareRecapCard";
// iter87.174 — Teams Per Crewmate lives ONLY on the /draft auto-draft
// slide now. Import removed from this file.
// iter87.9 — Danger Zone moved to its own tile in Tournament Admin.

export default function TournamentSettingsTab({
    league, leagueId, tournament, isLocked, adminIsPlayer, onChanged, participants,
    coAdminCtx: _coAdminCtx, onOpenComplete: _onOpenComplete,
}) {
    const [togglingParticipation, setTogglingParticipation] = useState(false);
    // iter87.199.12 — Picks Audit PDF export. Only shown when the
    // tournament uses boost logic (spread ladder or pot payout ladder).
    const [downloadingAudit, setDownloadingAudit] = useState(false);
    const boostEnabled = !!(tournament?.matchday_boost?.enabled);
    // iter59 — Per-tournament draft-lock check. The legacy global
    // `league.draft_id` would falsely lock unrelated tournaments in
    // a multi-tournament league. Trust the per-tournament dict only.
    const hasDraftForThisTournament = !!(tournament?.id && league?.drafts_by_tournament?.[tournament.id]);

    // iter87.6 — `handleClearDraft` moved into `TournamentDangerZoneCard`.

    const handleToggleParticipation = async () => {
        setTogglingParticipation(true);
        try {
            const res = await api.setAdminParticipation(leagueId, !adminIsPlayer);
            toast.success(res.is_player ? "You're now in the tournament" : "You're now opting out of playing");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update");
        }
        setTogglingParticipation(false);
    };

    // iter87.199.12 — Download the picks-audit PDF for this tournament.
    // Ungraded picks are omitted server-side; report is empty when no
    // picks have settled yet (still generates so admins have a paper
    // trail with the header + timestamp).
    const handleDownloadAudit = async () => {
        setDownloadingAudit(true);
        try {
            const { blob, filename } = await api.downloadPicksAuditPdf(tournament.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Picks audit downloaded");
        } catch (err) {
            const detail = err.response?.data;
            let msg = "Could not generate audit report";
            if (typeof detail === "string") msg = detail;
            else if (detail?.detail) msg = detail.detail;
            toast.error(msg);
        }
        setDownloadingAudit(false);
    };

    return (
        <div className="space-y-5" data-testid="tournament-settings-tab-card">
            {/* Banner explaining what's IN this scope */}
            <div className="brutal-card-light p-3 flex items-start gap-3 border-volt/40" data-testid="tournament-scope-banner">
                <div className="w-8 h-8 bg-volt border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                    <Trophy className="w-4 h-4 text-black" strokeWidth={2.5} />
                </div>
                <p className="text-xs text-zinc-300 leading-relaxed">
                    <strong className="text-volt">Tournament settings</strong> for <strong>{tournament?.name || "this tournament"}</strong> —
                    these only affect this tournament. For things that should apply across every tournament (invites, spectator mode, admin transfer),
                    switch to <strong>Rivalry Admin</strong>.
                </p>
            </div>

            <TournamentTimezoneCard
                tournament={tournament}
                isLocked={isLocked}
                onSaved={onChanged}
            />

            {/* iter87.9 — Danger Zone is its own tile now (Tournament
                Admin → Danger Zone). Mark-complete lives there ONLY,
                so Settings stays for identity + config. */}

            {/* iter87.174 — Teams Per Crewmate moved off Tournament
                Settings entirely. The setting now lives ONLY on the
                auto-draft slide inside the Draft Room (/draft) — one
                surface, no duplicated Save buttons, no split state.
                Pot pickem + golf pickem never needed it; draft-mode
                tournaments configure it moments before firing the
                draft, which is where the operator's attention already
                is. See iter87.174 CHANGELOG + AdminPanel Draft tile. */}

            {/* iter87.9 — Danger Zone is its own tile now (Tournament
                Admin → Danger Zone). Kept out of Settings so this
                surface stays for identity + config only. */}

            {isLocked && (
                <div className="brutal-card p-5 space-y-3 bg-blaze/10 border-blaze">
                    <div className="flex items-center gap-2">
                        <Flag className="w-5 h-5 text-blaze" />
                        <h2 className="font-display font-extrabold text-xl uppercase">Tournament — locked</h2>
                    </div>
                    <p className="text-xs text-zinc-300 leading-relaxed">
                        This tournament was marked complete{tournament.completed_at ? ` on ${new Date(tournament.completed_at).toLocaleString()}` : ""}. All edits are blocked.
                    </p>
                    <p className="text-xs text-zinc-400 leading-relaxed brutal-card-light p-3" data-testid="locked-contact-site-admin-note">
                        Need to unlock it? Reach out to your site admin — they're the only ones who can reopen a completed tournament. Send them the tournament name <span className="text-cyber font-bold">"{tournament.name}"</span> and they can flip it back from the Site Admin → Rivalries panel.
                    </p>
                </div>
            )}

            <div className="brutal-card p-5 space-y-4">
                <h2 className="font-display font-extrabold text-xl uppercase">Your participation</h2>
                <p className="text-xs text-zinc-400 leading-relaxed">
                    You're the rivalry admin. You can either play in this tournament too, or stay agnostic and just run it.
                    {hasDraftForThisTournament && <span className="text-blaze font-bold"> Locked once the draft starts — clear the draft below first to change.</span>}
                </p>
                <div className="flex items-center justify-between flex-wrap gap-3 brutal-card-light p-4">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-zinc-500">Currently</p>
                        <p className="font-display font-extrabold text-lg" data-testid="admin-participation-status">
                            {adminIsPlayer ? <span className="text-cyber">Playing</span> : <span className="text-blaze">Not playing</span>}
                        </p>
                    </div>
                    <button
                        data-testid="toggle-admin-participation-btn"
                        onClick={handleToggleParticipation}
                        disabled={togglingParticipation || hasDraftForThisTournament || isLocked}
                        className={`brutal-btn ${adminIsPlayer ? "bg-blaze text-white" : "bg-cyber text-black"} border-black shadow-brutal disabled:opacity-40`}
                    >
                        {togglingParticipation
                            ? "Saving..."
                            : (adminIsPlayer ? "Opt me out" : "I'm in — draft me too")}
                    </button>
                </div>
            </div>

            <RestoreSeedingCard league={league} tournament={tournament} onChanged={onChanged} />

            {/* iter87.199.26 — Email Season Recap to selected crewmates */}
            <ShareRecapCard tournament={tournament} />

            {/* iter87.199.12 — Picks Audit PDF. Only rendered for
                tournaments using boost logic (Matchday Boost / Weekly
                Spread Pick'em). Ungraded picks are omitted server-side. */}
            {boostEnabled && (
                <div className="brutal-card p-5 space-y-3" data-testid="picks-audit-card">
                    <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-cyber" />
                        <h2 className="font-display font-extrabold text-xl uppercase">Picks Audit Report</h2>
                    </div>
                    <p className="text-xs text-zinc-400 leading-relaxed">
                        Download a PDF audit of every crewmate's weekly picks that already have a
                        graded outcome. Future / ungraded picks are omitted — run it again any time
                        for a fresher paper trail.
                    </p>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">
                        Grouped by player, then by week. Each row shows the matchup, the pick, the
                        spread, the final score, the result (Win & Cover / Win / Loss & Cover / Loss),
                        and the points awarded. Weekly subtotals + a season total per player + a
                        Season Totals summary sorted by points close out the report.
                    </p>
                    <button
                        data-testid="download-picks-audit-btn"
                        onClick={handleDownloadAudit}
                        disabled={downloadingAudit}
                        className="brutal-btn bg-cyber text-black border-black shadow-brutal disabled:opacity-40 flex items-center gap-2"
                    >
                        <Download className="w-4 h-4" strokeWidth={2.5} />
                        {downloadingAudit ? "Generating…" : "Generate Audit Report"}
                    </button>
                </div>
            )}
        </div>
    );
}
